// src/screens/vuelosGuyana/VuelosGestorPropio.jsx
// Tarjeta que ve un gestor logueado (Tema 4) arriba del buscador de vuelos:
// su link para compartir, su comisión, el descuento que puede aplicarse a
// sí mismo (sale de su propia comisión, nunca del precio que cobra Yoedis)
// y su crédito acumulado. No se muestra a nadie que no sea gestor -- se
// resuelve consultando vuelos_gestores por su propio profile_id.
import React, { useState, useEffect } from 'react';
import { toast } from '../../components/UI';
import {
  getGestorPropio, setDescuentoGestor, getMisLeadsGestor, isVuelosTrackingOptedOut, setVuelosTrackingOptOut,
} from '../../services/vuelosGuyanaService';

const LINK_BASE = 'https://app.clicktravelagency.com.br/vuelos-guyana';

const ESTADO_LEAD = {
  pendiente: { label: 'Pendiente', color: 'var(--muted)' },
  cerrado: { label: 'Te debe', color: 'var(--gold)' },
  pagado: { label: '✅ Pagado', color: 'var(--success)' },
};

const formatFechaLead = (iso) =>
  new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' });

export default function VuelosGestorPropio({ uid }) {
  const [gestor, setGestor] = useState(null);
  const [descuento, setDescuento] = useState('0');
  const [guardando, setGuardando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  // Mismo botón que tiene Yoedis en Stats -- así el gestor no se cuenta a sí
  // mismo cuando abre su propio link para probarlo, y Yoedis no tiene que
  // hacerlo por cada uno a mano.
  const [noContarme, setNoContarme] = useState(isVuelosTrackingOptedOut());
  const toggleNoContarme = () => {
    const nuevo = !noContarme;
    setVuelosTrackingOptOut(nuevo);
    setNoContarme(nuevo);
  };

  const cargar = async () => {
    if (!uid) { setLoading(false); return; }
    const [g, l] = await Promise.all([getGestorPropio(uid), getMisLeadsGestor()]);
    setGestor(g);
    setLeads(l);
    if (g) setDescuento(String(g.descuento_actual_usd));
    setLoading(false);
  };
  useEffect(() => { cargar(); }, [uid]);

  if (loading || !gestor) return null;

  // Un toque es solo alguien que abrió WhatsApp desde su link; no cuenta
  // como solicitud hasta que Yoedis confirma que esa persona sí escribió.
  const toques = leads.filter((l) => l.lead_estado === 'toque');
  const solicitudes = leads.filter((l) => l.lead_estado !== 'toque');

  const link = `${LINK_BASE}?g=${gestor.codigo}`;
  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast('Link copiado 📋');
    } catch {
      toast('No se pudo copiar automáticamente -- mantén presionado el link para copiarlo.', 'error');
    }
  };

  const handleGuardarDescuento = async () => {
    const monto = parseFloat(descuento) || 0;
    if (monto < 0 || monto > gestor.comision_max_usd) {
      return toast(`El descuento tiene que estar entre 0 y $${gestor.comision_max_usd}.`, 'error');
    }
    setGuardando(true);
    try {
      await setDescuentoGestor(monto);
      toast('Descuento actualizado ✅');
      cargar();
    } catch { toast('Error al guardar.', 'error'); }
    setGuardando(false);
  };

  return (
    <div style={{
      margin: '14px 16px 0', padding: 16, borderRadius: 14,
      background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderLeft: '3px solid var(--orange)',
    }}>
      <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--white)', marginBottom: 4 }}>🤝 Tu panel de gestor</p>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Ganas hasta ${gestor.comision_max_usd} por cada venta que traigas. Comparte tu link tal cual — cuando el cliente escriba por WhatsApp, la venta queda marcada como tuya automáticamente.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'stretch' }}>
        <a href={link} target="_blank" rel="noopener noreferrer" style={{
          flex: 1, display: 'block', background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.25)',
          borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--off-white)', fontFamily: 'monospace', textDecoration: 'none', wordBreak: 'break-all',
        }}>🔗 {link}</a>
        <button onClick={copiarLink} title="Copiar link" style={{
          flexShrink: 0, background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.25)',
          borderRadius: 8, padding: '0 14px', fontSize: 16, color: 'var(--orange)',
        }}>📋</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div style={{ background: 'rgba(0,196,140,0.08)', border: '1px solid rgba(0,196,140,0.25)', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
          <p style={{ fontSize: 9, color: 'var(--muted)' }}>COMISIÓN MÁX.</p>
          <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--success)' }}>${gestor.comision_max_usd}</p>
        </div>
        <div style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.25)', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
          <p style={{ fontSize: 9, color: 'var(--muted)' }}>POR COBRAR</p>
          <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--orange)' }}>${gestor.credito.toFixed(0)}</p>
        </div>
      </div>

      <label style={{ fontSize: 11, color: 'var(--muted)' }}>
        Descuento que le regalás a tu próximo cliente (sale de tu comisión)
      </label>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input
          type="number" inputMode="decimal" value={descuento} onChange={(e) => setDescuento(e.target.value)}
          min={0} max={gestor.comision_max_usd} style={{ flex: 1 }}
        />
        <button onClick={handleGuardarDescuento} disabled={guardando} style={{
          background: 'var(--navy)', border: '1px solid var(--orange)', color: 'var(--orange)',
          borderRadius: 8, padding: '0 16px', fontSize: 12, fontWeight: 700,
        }}>{guardando ? '...' : 'Guardar'}</button>
      </div>
      <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6, lineHeight: 1.4 }}>
        Con ${descuento || 0} de descuento, ganarías ${Math.max(0, gestor.comision_max_usd - (parseFloat(descuento) || 0))} en la próxima venta. Déjalo en 0 para volver a tu comisión completa.
      </p>

      <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 16, marginBottom: 4, fontWeight: 700 }}>
        TOQUES EN WHATSAPP{toques.length > 0 ? ` (${toques.length})` : ''}
      </p>
      <p style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 12 }}>
        Personas que tocaron WhatsApp desde tu link. No todas llegan a escribir.
      </p>

      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, fontWeight: 700 }}>
        TUS SOLICITUDES{solicitudes.length > 0 ? ` (${solicitudes.length})` : ''}
      </p>
      {solicitudes.length === 0 ? (
        <p style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
          Todavía nadie escribió por WhatsApp desde tu link. En cuanto alguien lo haga, aparece acá.
        </p>
      ) : (
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {solicitudes.map((l) => {
            const estado = ESTADO_LEAD[l.lead_estado] || ESTADO_LEAD.pendiente;
            return (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--navy-border)' }}>
                <span style={{ fontSize: 11, color: 'var(--off-white)' }}>
                  {formatFechaLead(l.created_at)} · {l.ciudad_salida && l.destino ? `${l.ciudad_salida} → ${l.destino}` : 'Consulta general'}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: estado.color }}>
                  {estado.label}{l.lead_monto_usd != null ? ` $${parseFloat(l.lead_monto_usd).toFixed(0)}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{
        marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--navy-border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
      }}>
        <div>
          <p style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--white)' }}>No contarme cuando pruebo mi link</p>
          <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, lineHeight: 1.4 }}>
            Solo aplica a este teléfono/navegador -- si abres tu link para revisarlo, no queda como una visita de un cliente.
          </p>
        </div>
        <button onClick={toggleNoContarme} style={{
          flexShrink: 0, fontSize: 10.5, fontWeight: 700, borderRadius: 20, padding: '6px 10px', whiteSpace: 'nowrap',
          background: noContarme ? 'rgba(0,196,140,0.15)' : 'var(--navy)',
          color: noContarme ? 'var(--success)' : 'var(--muted)',
          border: `1px solid ${noContarme ? 'rgba(0,196,140,0.4)' : 'var(--navy-border)'}`,
        }}>{noContarme ? '✅ Activado' : 'Desactivado'}</button>
      </div>
    </div>
  );
}
